<script setup>
import { reactive, computed, watch, ref, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, requiredIf } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import CampaignsAPI from 'dashboard/api/campaigns';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
};

const initialState = {
  title: '',
  inboxId: null,
  templateId: null,
  sendNow: false,
  scheduledAt: null,
  selectedAudience: [],
};

const state = reactive({ ...initialState });
const templateParserRef = ref(null);

const rules = {
  title: { required, minLength: minLength(1) },
  inboxId: { required },
  templateId: { required },
  scheduledAt: { required: requiredIf(() => !state.sendNow) },
  selectedAudience: { required },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  const templates = formState.getFilteredWhatsAppTemplates.value(state.inboxId);
  return templates.map(template => {
    // Create a more user-friendly label from template name
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template: template,
    };
  });
});

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(option => option.value === state.templateId)
    ?.template;
});

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.WHATSAPP.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  template: getErrorMessage('templateId', 'TEMPLATE'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

// Bloqueia o envio enquanto houver variável obrigatória do template sem
// preencher. Consulta o isFormInvalid do parser — o v$/allKeysRequired dele não
// desce nos campos aninhados de body, então não serve como fonte de verdade.
// Ver task 86aj5z4m5.
const isTemplateInvalid = computed(
  () => !!templateParserRef.value?.isFormInvalid
);

const isSubmitDisabled = computed(
  () => v$.value.$invalid || isTemplateInvalid.value
);

// --- Estimativa de investimento antes do disparo (86ajefd0h) ---
// Um template tem categoria unica (Marketing OU Utility), entao o custo e
// (contatos da audiencia) x (tarifa da categoria). Mostramos em USD, que e como a Meta
// cobra hoje. O backend (CampaignEstimateService) conta os contatos com a MESMA query do
// disparo, pra estimativa e envio real baterem.
const estimate = ref(null);
const isEstimating = ref(false);
let estimateTimer = null;

const selectedCategory = computed(
  () => selectedTemplate.value?.category ?? null
);

const fetchEstimate = async () => {
  const audience =
    state.selectedAudience?.map(id => ({ id, type: 'Label' })) ?? [];
  if (!selectedCategory.value || audience.length === 0) {
    estimate.value = null;
    return;
  }
  isEstimating.value = true;
  try {
    const { data } = await CampaignsAPI.estimate({
      audience,
      category: selectedCategory.value,
    });
    estimate.value = data;
  } catch (error) {
    estimate.value = null;
  } finally {
    isEstimating.value = false;
  }
};

// Recalcula quando muda a audiencia (contagem) ou o template (categoria/tarifa),
// com debounce pra nao bater no backend a cada clique de etiqueta.
watch(
  [() => state.selectedAudience, () => state.templateId],
  () => {
    clearTimeout(estimateTimer);
    estimateTimer = setTimeout(fetchEstimate, 400);
  },
  { deep: true }
);

onBeforeUnmount(() => clearTimeout(estimateTimer));

const usdFormatter = digits =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const estimatedCostLabel = computed(() =>
  estimate.value?.priced
    ? usdFormatter(2).format(estimate.value.estimated_cost)
    : null
);

const unitPriceLabel = computed(() =>
  estimate.value?.priced
    ? usdFormatter(4).format(estimate.value.unit_price)
    : null
);

const audienceCountLabel = computed(() =>
  estimate.value
    ? new Intl.NumberFormat('pt-BR').format(estimate.value.audience_count)
    : ''
);

const estimateCategoryLabel = computed(() => {
  const category = estimate.value?.category;
  if (!category) return '';
  return t(
    `CAMPAIGN.WHATSAPP.CREATE.FORM.ESTIMATE.CATEGORY.${category}`,
    category
  );
});

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, initialState);
  v$.value.$reset();
  estimate.value = null;
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => {
  // Find the selected template to get its content
  const currentTemplate = selectedTemplate.value;
  const parserData = templateParserRef.value;

  // Extract template content - this should be the template message body
  const templateContent = parserData?.renderedTemplate || '';

  // Prepare template_params object with the same structure as used in contacts
  const templateParams = {
    name: currentTemplate?.name || '',
    namespace: currentTemplate?.namespace || '',
    category: currentTemplate?.category || 'UTILITY',
    language: currentTemplate?.language || 'en_US',
    processed_params: parserData?.processedParams || {},
  };

  return {
    title: state.title,
    message: templateContent,
    template_params: templateParams,
    inbox_id: state.inboxId,
    // "Enviar agora": send a null scheduled_at; the backend defaults it to now and
    // the controller enqueues the dispatch immediately.
    scheduled_at: state.sendNow ? null : formatToUTCString(state.scheduledAt),
    audience: state.selectedAudience?.map(id => ({
      id,
      type: 'Label',
    })),
  };
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  // Marca o parser como "tocado" para exibir os destaques e o resumo de
  // variáveis faltantes caso o usuário force o envio.
  templateParserRef.value?.v$?.$touch();
  if (!isFormValid || isTemplateInvalid.value) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};

// Reset template selection when inbox changes
watch(
  () => state.inboxId,
  () => {
    state.templateId = null;
  }
);
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label for="template" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
      </label>
      <ComboBox
        id="template"
        v-model="state.templateId"
        :options="templateOptions"
        :has-error="!!formErrors.template"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
        :message="formErrors.template"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.INFO') }}
      </p>
    </div>

    <!-- Template Parser -->
    <WhatsAppTemplateParser
      v-if="selectedTemplate"
      ref="templateParserRef"
      :template="selectedTemplate"
    />

    <div class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <div class="flex gap-3 justify-between items-start">
      <div class="flex flex-col gap-0.5">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_NOW.LABEL') }}
        </label>
        <p class="mt-0.5 text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_NOW.INFO') }}
        </p>
      </div>
      <Switch v-model="state.sendNow" class="mt-1" />
    </div>

    <Input
      v-if="!state.sendNow"
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <!-- Estimativa de investimento antes do disparo (86ajefd0h) -->
    <div
      v-if="estimate?.priced"
      class="flex flex-col gap-1 p-3 rounded-xl bg-n-alpha-1"
    >
      <div class="flex gap-2 justify-between items-center">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.ESTIMATE.LABEL') }}
        </span>
        <span class="text-base font-semibold text-n-slate-12">
          {{ estimatedCostLabel }}
        </span>
      </div>
      <p class="text-xs text-n-slate-11">
        {{
          t('CAMPAIGN.WHATSAPP.CREATE.FORM.ESTIMATE.BREAKDOWN', {
            count: audienceCountLabel,
            unit: unitPriceLabel,
            category: estimateCategoryLabel,
          })
        }}
      </p>
      <p class="text-xs text-n-slate-10">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.ESTIMATE.DISCLAIMER') }}
      </p>
    </div>
    <p v-else-if="isEstimating" class="text-xs text-n-slate-11">
      {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.ESTIMATE.LOADING') }}
    </p>

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
